import {
  useCallback,
  useEffect,
  Dispatch,
  useState,
  SetStateAction,
} from 'react';
import errorConfig from '../config.error.json';
import {
  saveRequest,
  IError,
  ISaveRequestInput,
  ErrorCode,
  ISaveRequestOutput,
  useUserContext,
  useFeatureContext,
  ResponseCSOSHeaders,
  IICAMToken,
  mapUserAuthenticated,
  mapICAMTokenToUser,
  IDeaEditMode,
  saveDocument,
} from '../common';
import { useSubmitRequestFlow } from '../login';
import {
  IRequestApiCallStatus,
  IRequestState,
  RequestAction,
  requestRequireDocument,
} from '.';
import { find, map as lmap, noop } from 'lodash/fp';
import { pathOr, always } from 'rambda';
import { fold, left, map, right } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/lib/function';

export function useSubmitRequest({
  requestState,
  dispatchRequest,
}: {
  requestState: IRequestState;
  dispatchRequest: Dispatch<RequestAction>;
}) {
  const { user, setUser } = useUserContext();
  const { feature } = useFeatureContext();
  const submitRequestFlow = useSubmitRequestFlow();
  const [saveRequestCallInProgress, setsaveRequestCallInProgress] =
    useState(false);

  useEffect(() => {
    flow(
      getRequest({ saveRequestCallInProgress, submitRequestFlow }),
      map<{ request: ISaveRequestInput; file?: File }, void>(
        handleRequest(feature.sailpoint)(
          pathOr(
            {
              'content-type': '',
              authorization: '',
              'x-csrf-csos-token': '',
            },
            'icam.headers'
          )(user)
        )({ setsaveRequestCallInProgress, dispatchRequest })
      )
    )(requestState);
  }, [
    feature.sailpoint,
    user,
    submitRequestFlow,
    requestState,
    dispatchRequest,
    saveRequestCallInProgress,
  ]);

  const handleSubmit = (status: string) => () => {
    dispatchRequest({
      type: 'SaveRequest',
      payload: status,
    });
  };

  const refreshToken = useCallback(() => {
    const failRefreshToken = flow(
      () =>
        dispatchRequest({
          type: 'SaveRequestError',
          payload: {
            code: ErrorCode.RefreshTokenError,
            message: errorConfig.refreshTokenFailed,
          },
        }),
      always(false)
    );

    const handleRefreshTokenSuccess = fold<IError, IICAMToken, boolean>(
      failRefreshToken,
      flow(
        mapICAMTokenToUser,
        setUser,
        always(mapUserAuthenticated(true)),
        setUser,
        always(true)
      )
    );

    return user.refreshToken().then(handleRefreshTokenSuccess);
  }, [dispatchRequest, setUser, user]);

  return { handleSubmit, refreshToken };
}

const getRequest =
  ({
    saveRequestCallInProgress,
    submitRequestFlow,
  }: {
    saveRequestCallInProgress: boolean;
    submitRequestFlow: boolean;
  }) =>
  (requestState: IRequestState) => {
    const apiCallStatus = find<IRequestApiCallStatus>({
      type: 'SaveRequest',
      apiCallStatus: null,
    })(requestState.apiCallStatus);

    // block duplicate saveRequest
    if (
      apiCallStatus === void 0 ||
      submitRequestFlow ||
      saveRequestCallInProgress
    ) {
      return left(void 0);
    }

    if (requestState.request === void 0) {
      return left(void 0);
    } else {
      return right({ request: requestState.request, file: requestState.file });
    }
  };

const toUppercase = (a: IDeaEditMode): IDeaEditMode => {
  return {
    ...a,
    deaNum: a.deaNum.toUpperCase(),
  };
};

const handleRequest =
  (sailpoint: boolean) =>
  (hR: ResponseCSOSHeaders) =>
  ({
    setsaveRequestCallInProgress,
    dispatchRequest,
  }: {
    setsaveRequestCallInProgress: Dispatch<SetStateAction<boolean>>;
    dispatchRequest: Dispatch<RequestAction>;
  }) =>
    flow(
      ({ request, file }: { request: ISaveRequestInput; file?: File }) => ({
        request: {
          ...request,
          deaInfos: lmap<IDeaEditMode, IDeaEditMode>(toUppercase)(
            request.deaInfos
          ),
        },
        file,
      }),
      ({ request, file }) =>
        saveRequest(sailpoint)(hR)(request).then(
          flow(
            fold<IError, ISaveRequestOutput, void>(
              payload =>
                dispatchRequest({
                  type: 'SaveRequestError',
                  payload,
                }),
              flow(
                b =>
                  dispatchRequest({
                    type: 'SaveRequestSuccess',
                    payload: b,
                  }),
                requestRequireDocument(request) && file !== void 0
                  ? () => {
                      saveDocument(sailpoint)(hR)(request)(file)
                        .then(
                          fold<boolean, boolean, void>(
                            () =>
                              dispatchRequest({
                                type: 'SaveFileError',
                                payload: void 0,
                              }),
                            () => {
                              dispatchRequest({
                                type: 'SaveFileSuccess',
                                payload: void 0,
                              });
                            }
                          )
                        )
                        .catch(() =>
                          dispatchRequest({
                            type: 'SaveFileError',
                            payload: void 0,
                          })
                        );
                    }
                  : noop
              )
            ),
            () => setsaveRequestCallInProgress(false)
          )
        ),
      () => setsaveRequestCallInProgress(true)
    );
